import { Button } from '@/components/Atoms/Button/Button';
import popoverStyles from '@/components/Atoms/Tooltip/PopoverContent.module.css';
import { useDialog } from '@/components/Molecules/Dialog/Dialog';
import { LimitReachedDialog } from '@/components/Molecules/LimitReachedDialog/LimitReachedDialog';
import { FeatureFlagWrapper } from '@/components/Wrappers/FeatureFlag/FeatureFlag';
import { useAppContext } from '@/contexts/AppContext/AppContext';
import { useHintPopover } from '@/hooks/useHintPopover';
import { canUseOfflineMode, deleteOfflineItem, downloadItemForOfflineReading } from '@/services/OfflineStorage';
import { OFFLINE_READING_FEATURE_FLAG } from '@/services/OfflineStorage/constants';
import { getItemFromDb } from '@/services/OfflineStorage/db';
import { isItemExpired } from '@/services/OfflineStorage/license';
import { ensureOfflineReady } from '@/services/OfflineStorage/serviceWorker';
import type { OfflineDownloadArgs } from '@/services/OfflineStorage/types';
import { toast } from '@/services/Toast/toastStore';
import CheckCircleIcon from '@bloomberg/web-icons-react/CheckCircle';
import DownloadIcon from '@bloomberg/web-icons-react/Download';
import type { FC, ReactNode } from 'react';
import { useEffect, useId, useMemo, useState } from 'react';
export interface IOfflineDownloadButtonProps extends OfflineDownloadArgs {
  canDownload?: boolean;
}
type Mode = 'saved' | 'limitReached' | 'expired' | 'notSaved';
type ModeConfig = {
  icon: ReactNode;
  label: string;
  tooltip: string;
  onClick: () => void | Promise<void>;
};
export const OfflineDownloadButton: FC<IOfflineDownloadButtonProps> = ({
  contentType,
  itemId,
  title,
  viewerUrl,
  pdfUrl,
  watermarkText,
  canDownload = true
}) => {
  const {
    hasFeature,
    user
  } = useAppContext();
  const {
    popoverRef,
    openPopover,
    closePopover
  } = useHintPopover();
  const tooltipId = useId();
  const dialog = useDialog({
    lazy: true
  });
  const [savedStatus, setSavedStatus] = useState<'saved' | 'expired' | null>();
  const [updating, setUpdating] = useState(false);
  const featureEnabled = !!user && !user.isGuestUser && hasFeature(OFFLINE_READING_FEATURE_FLAG) && canUseOfflineMode();
  useEffect(() => {
    if (!featureEnabled || !user) return;
    let cancelled = false;
    getItemFromDb(user.id, contentType, itemId).then(item => {
      if (!cancelled) {
        if (!item) setSavedStatus(null);else if (!item.pdf || isItemExpired(item.downloadedAt)) setSavedStatus('expired');else setSavedStatus('saved');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [featureEnabled, user, contentType, itemId]);
  const mode = useMemo<Mode | null>(() => {
    if (savedStatus === undefined) {
      return null;
    }
    if (savedStatus === 'saved') {
      return 'saved';
    }
    if (!canDownload) {
      return 'limitReached';
    }
    if (savedStatus === 'expired') {
      return 'expired';
    }
    return 'notSaved';
  }, [savedStatus, canDownload]);
  const save = async () => {
    if (!user) return;
    setUpdating(true);
    // On a first visit the service worker may still be precaching the offline shell. Start it alongside the download
    // and wait for it (up to a limit) before reporting success, so "Saved" means offline reading actually works.
    // A failed download doesn't wait for it.
    const offlineReady = ensureOfflineReady().catch((err: unknown) => console.warn('[SW] Offline mode not ready:', err));

    // TODO: Analytics + TRAN
    try {
      if (mode === 'expired') await deleteOfflineItem(user.id, contentType, itemId);
      await downloadItemForOfflineReading(user.id, {
        contentType,
        itemId,
        title,
        viewerUrl,
        pdfUrl,
        watermarkText
      });
    } catch {
      setUpdating(false);
      toast.error("Couldn't save for offline reading");
      return;
    }
    await offlineReady;
    setUpdating(false);
    setSavedStatus('saved');
    toast.success('Saved for offline reading');
  };
  const remove = async () => {
    if (!user) return;
    setUpdating(true);
    try {
      await deleteOfflineItem(user.id, contentType, itemId);
      setSavedStatus(null);
      // TODO: Analytics + TRAN
      toast.success('Removed from offline reading');
    } catch {
      toast.error("Couldn't remove from offline reading");
    } finally {
      setUpdating(false);
    }
  };
  if (!featureEnabled || !mode) return null;
  const MODE_CONFIG: Record<Mode, ModeConfig> = {
    saved: {
      icon: <CheckCircleIcon />,
      label: 'Saved offline',
      tooltip: 'Remove from offline reading',
      onClick: remove
    },
    limitReached: {
      icon: <DownloadIcon />,
      label: 'Save offline',
      tooltip: 'Download limit reached',
      onClick: () => dialog.open()
    },
    expired: {
      icon: <DownloadIcon />,
      label: 'Download again',
      tooltip: 'Your offline copy has expired',
      onClick: save
    },
    notSaved: {
      icon: <DownloadIcon />,
      label: 'Save offline',
      tooltip: 'Save for offline reading',
      onClick: save
    }
  };
  const {
    icon,
    label,
    tooltip,
    onClick
  } = MODE_CONFIG[mode];
  return <FeatureFlagWrapper feature={OFFLINE_READING_FEATURE_FLAG}>
            <div className={popoverStyles.anchorScope}>
                <Button kind="tertiary" icon={icon} aria-label={label} isLoading={updating} disabled={updating} onClick={onClick} aria-pressed={mode === 'saved'} aria-describedby={tooltipId} className={popoverStyles.anchorTrigger} onMouseEnter={openPopover} onMouseLeave={closePopover} onFocus={openPopover} onBlur={closePopover}>
                    {updating ? 'Loading...' : label}
                </Button>
                <span id={tooltipId} ref={popoverRef} role="tooltip" popover="hint" className={popoverStyles.popover}>
                    <span className={popoverStyles.popoverInner}>{tooltip}</span>
                </span>
            </div>
            {mode === 'limitReached' && <LimitReachedDialog dialog={dialog} limitMessageKey="insight" />}
        </FeatureFlagWrapper>;
};